/**
 * Giphy API Composable
 *
 * Provides methods to search and fetch trending GIFs from Giphy.
 * Requires VITE_GIPHY_API_KEY in environment.
 */

import { ref } from 'vue'

const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY as string
const GIPHY_API_BASE = 'https://api.giphy.com/v1/gifs'

/**
 * GIF data structure for chat messages
 */
export interface GiphyGif {
  id: string
  title: string
  url: string         // Full size GIF URL
  preview_url: string // Small preview for picker grid
  width: number
  height: number
}

/**
 * Giphy API response structure
 */
interface GiphyApiResponse {
  data: Array<{
    id: string
    title: string
    images: {
      fixed_height: {
        url: string
        width: string
        height: string
      }
      fixed_height_small: {
        url: string
        width: string
        height: string
      }
      original: {
        url: string
        width: string
        height: string
      }
    }
  }>
}

/**
 * Transform Giphy API response to our GIF structure
 */
function transformGiphyResponse(data: GiphyApiResponse['data']): GiphyGif[] {
  return data.map((gif) => ({
    id: gif.id,
    title: gif.title,
    url: gif.images.fixed_height.url,
    preview_url: gif.images.fixed_height_small.url,
    width: parseInt(gif.images.fixed_height.width, 10),
    height: parseInt(gif.images.fixed_height.height, 10),
  }))
}

/**
 * Giphy API composable
 */
export function useGiphy() {
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Search for GIFs by query
   */
  async function searchGifs(query: string, limit = 20): Promise<GiphyGif[]> {
    if (!GIPHY_API_KEY) {
      error.value = 'Giphy API key not configured'
      console.warn('[useGiphy] VITE_GIPHY_API_KEY is not set')
      return []
    }

    if (!query.trim()) {
      return []
    }

    isLoading.value = true
    error.value = null

    try {
      const params = new URLSearchParams({
        api_key: GIPHY_API_KEY,
        q: query.trim(),
        limit: limit.toString(),
        rating: 'pg-13', // Safe for all audiences
        lang: 'en',
      })

      const response = await fetch(`${GIPHY_API_BASE}/search?${params}`)

      if (!response.ok) {
        throw new Error(`Giphy API error: ${response.status}`)
      }

      const data: GiphyApiResponse = await response.json()
      return transformGiphyResponse(data.data)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to search GIFs'
      console.error('[useGiphy] Search error:', err)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get trending GIFs
   */
  async function getTrendingGifs(limit = 20): Promise<GiphyGif[]> {
    if (!GIPHY_API_KEY) {
      error.value = 'Giphy API key not configured'
      console.warn('[useGiphy] VITE_GIPHY_API_KEY is not set')
      return []
    }

    isLoading.value = true
    error.value = null

    try {
      const params = new URLSearchParams({
        api_key: GIPHY_API_KEY,
        limit: limit.toString(),
        rating: 'pg-13',
      })

      const response = await fetch(`${GIPHY_API_BASE}/trending?${params}`)

      if (!response.ok) {
        throw new Error(`Giphy API error: ${response.status}`)
      }

      const data: GiphyApiResponse = await response.json()
      return transformGiphyResponse(data.data)
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch trending GIFs'
      console.error('[useGiphy] Trending error:', err)
      return []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Validate that a URL is from Giphy (security check)
   */
  function isValidGiphyUrl(url: string): boolean {
    try {
      const parsed = new URL(url)
      return (
        parsed.protocol === 'https:' &&
        (parsed.hostname === 'media.giphy.com' ||
          parsed.hostname.endsWith('.giphy.com'))
      )
    } catch {
      return false
    }
  }

  /**
   * Check if API key is configured
   */
  function isConfigured(): boolean {
    return Boolean(GIPHY_API_KEY)
  }

  return {
    // State
    isLoading,
    error,

    // Methods
    searchGifs,
    getTrendingGifs,
    isValidGiphyUrl,
    isConfigured,
  }
}
